/**
 * The two donut-chart cards in the Portal module ("Nach Status" / "Nach Rolle").
 * Inline SVG circle-arc donuts with the verbatim stroke-dasharray /
 * stroke-dashoffset numbers from the prototype (Hausverwaltung Hero.dc.html,
 * lines 1055–1097).
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

type LegendRow = {
  readonly swatch: string;
  readonly label: string;
  readonly value: string;
};

/** Mono eyebrow above a donut legend. */
function LegendHead(props: { readonly children: ReactNode }): ReactNode {
  return (
    <div
      style={{
        fontFamily: H.label,
        fontSize: 10,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(11,15,25,0.4)",
        marginBottom: 9,
      }}
    >
      {props.children}
    </div>
  );
}

/** Single legend line: square swatch + label + tabular value. */
function LegendItem(props: LegendRow): ReactNode {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 2,
          background: props.swatch,
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, color: "rgba(11,15,25,0.66)" }}>
        {props.label}
      </span>
      <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
        {props.value}
      </span>
    </div>
  );
}

/** Centered big number + caption inside the donut hole. */
function DonutCenter(props: {
  readonly value: string;
  readonly caption: string;
}): ReactNode {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          fontSize: 17,
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {props.value}
      </span>
      <span style={{ fontSize: 9, color: "rgba(11,15,25,0.45)", marginTop: 1 }}>
        {props.caption}
      </span>
    </div>
  );
}

/** Card shell holding a donut SVG (left) and a legend (right). */
function DonutCard(props: {
  readonly svg: ReactNode;
  readonly center: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div
      style={{
        flex: 1,
        border: "1px solid #ECEEF1",
        borderRadius: 12,
        padding: "13px 16px",
        display: "flex",
        alignItems: "center",
        gap: 15,
      }}
    >
      <div
        style={{ position: "relative", width: 88, height: 88, flexShrink: 0 }}
      >
        {props.svg}
        {props.center}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{props.children}</div>
    </div>
  );
}

/** Both Portal donut cards laid out side by side. */
export function PortalDonuts(): ReactNode {
  const t = useTranslations("marketing.modulePortalDonuts");
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
      <DonutCard
        center={<DonutCenter value="1.864" caption={t("zugaenge")} />}
        svg={
          <svg
            width="88"
            height="88"
            viewBox="0 0 88 88"
            style={{ display: "block" }}
            aria-hidden="true"
          >
            <g transform="rotate(-90 44 44)">
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="8"
              />
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                style={{ stroke: H.accent }}
                strokeWidth="8"
                strokeDasharray="162.33 89.00"
                strokeDashoffset="0"
              />
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                stroke="#E0A23B"
                strokeWidth="8"
                strokeDasharray="52.04 199.29"
                strokeDashoffset="-162.33"
              />
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                stroke="#C9743A"
                strokeWidth="8"
                strokeDasharray="5.66 245.67"
                strokeDashoffset="-214.37"
              />
            </g>
          </svg>
        }
      >
        <LegendHead>{t("status")}</LegendHead>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <LegendItem swatch={H.accent} label={t("zugang")} value="1.204" />
          <LegendItem swatch="#E0A23B" label={t("ausstehend")} value="386" />
          <LegendItem swatch="#C9743A" label={t("abgelaufen")} value="42" />
          <LegendItem
            swatch="#D7DAE0"
            label={t("nichtEingeladen")}
            value="232"
          />
        </div>
      </DonutCard>

      <DonutCard
        center={<DonutCenter value="1.864" caption={t("kontakte")} />}
        svg={
          <svg
            width="88"
            height="88"
            viewBox="0 0 88 88"
            style={{ display: "block" }}
            aria-hidden="true"
          >
            <g transform="rotate(-90 44 44)">
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                style={{ stroke: H.accentMid }}
                strokeWidth="8"
              />
              <circle
                cx="44"
                cy="44"
                r="40"
                fill="none"
                style={{ stroke: H.accent }}
                strokeWidth="8"
                strokeDasharray="134.29 117.04"
                strokeDashoffset="0"
              />
            </g>
          </svg>
        }
      >
        <LegendHead>{t("rolle")}</LegendHead>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <LegendItem swatch={H.accent} label={t("eigentuemer")} value="996" />
          <LegendItem swatch={H.accentMid} label={t("mieter")} value="868" />
          <div
            style={{
              fontSize: 11.5,
              color: "rgba(11,15,25,0.4)",
              marginTop: 5,
            }}
          >
            {t("k43KontakteJe")}
          </div>
        </div>
      </DonutCard>
    </div>
  );
}
